<template>
  <div class="ma-0 mx-auto" style="min-height: 100%; max-width: 1000px;">
    <h2 class="mt-0 mb-5" style="color: white;">My Property</h2>

    <div v-if="noProperty === false">
      <alerts-panel v-if="overduePaymentsProp.length > 0 || upcomingPaymentsProp.length > 0" :env="env"
        :error="overduePaymentsProp" :warning="upcomingPaymentsProp" />

      <v-card v-if="property" class="mt-5 mx-auto pa-5" style="width: 100%;">
        <div style="width: 100%;" class="d-flex flex-column jusity-space-between align-center mb-3">
          <h2 class="mr-auto text-left">Property Info</h2>

          <h4 v-if="property.property_url" class="mr-auto text-left">
            <a :href="getPropertyURL(property.property_url)" target="_blank">Whatcom County Property Page</a>
          </h4>
        </div>

        <div class="d-flex flex-column justify-space-between
          flex-md-row" style="line-height: 1.7rem; font-family: var(--heading-font) !important;">
          <div>
            <h3 class="text-left" style="font-weight: bold;
              color: #222;">Property Details</h3>
            <div>Lot # {{ property.lot }}</div>
            <div>{{ property.member_of_record }}</div>
            <div>{{ property.email }}</div>
            <div>{{ toTel(property.phone) }}</div>
          </div>

          <br>

          <div class="text-left text-md-right mt-5 mt-md-0">
            <h3 class="text-md-right text-left" style="font-weight: bold;
              color: #222;">Property Address</h3>
            <div>{{ property.line1 }}</div>
            <div>{{ property.line2 }}</div>

            <br v-if="property.license_plates">

            <h3 v-if="property.license_plates" class="text-left text-md-right" style="font-weight: bold;
              color: #222;">License Plates</h3>

            <div v-if="property.license_plates">{{ property.license_plates }}</div>
          </div>
        </div>

      </v-card>

      <!-- Registered Guests Section -->
      <v-card v-if="env.registeredGuestsActive && property" class="mt-5 mx-auto pa-5" style="width: 100%;">
        <div class="guests-header">
          <h2>{{ env.registeredGuestsTabName || 'Registered Guests' }}</h2>
          <p class="guests-description">
            Manage guests who are registered for facility access at this property.
          </p>
        </div>

        <div v-if="filteredGuests && filteredGuests.length > 0" class="guests-container">
          <div v-for="(guest, i) in filteredGuests" :key="'guest-' + i" class="guest-card">
            <div class="guest-info">
              <div class="guest-name">{{ guest.fname }} {{ guest.lname }}</div>
              <div v-if="guest.affiliation" class="guest-affiliation">{{ guest.affiliation }}</div>
              <div class="guest-meta">
                <span>Added: {{ formatDate(guest.created_at) }}</span>
                <span class="meta-divider">•</span>
                <span>Modified: {{ formatDate(guest.modified_on || guest.created_at) }}</span>
                <span v-if="guestDaysRemaining(guest) > 0" class="guest-locked-badge">
                  <span class="meta-divider">•</span>
                  Locked for {{ guestDaysRemaining(guest) }} day(s)
                </span>
              </div>
            </div>

            <div class="guest-actions">
              <v-btn icon small :disabled="!canEditGuest(guest)"
                :title="canEditGuest(guest) ? 'Edit' : 'Locked: ' + guestDaysRemaining(guest) + ' day(s)'"
                @click.stop="canEditGuest(guest) && editGuest(guest)">
                <v-icon small :color="canEditGuest(guest) ? 'primary' : ''">mdi-pencil</v-icon>
              </v-btn>

              <v-btn icon small :disabled="!canEditGuest(guest)"
                :title="canEditGuest(guest) ? 'Delete' : 'Locked: ' + guestDaysRemaining(guest) + ' day(s)'"
                @click.stop="canEditGuest(guest) && deleteGuest(guest)">
                <v-icon small :color="canEditGuest(guest) ? 'error' : ''">mdi-delete</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <div v-else class="guests-empty">
          No {{ (env.registeredGuestsTabName || 'registered guests').toLowerCase() }} for this property.
        </div>

        <div class="guests-footer">
          <v-btn v-if="canAddGuest" color="accent" @click="addGuest()">
            <v-icon class="mr-2">mdi-plus</v-icon>
            {{ 'Add ' + registeredGuestsSingular }}
          </v-btn>

          <div v-else-if="maxGuestsReached" class="max-guests-message">
            You have reached the maximum of {{ maxRegisteredGuests }} {{ (env.registeredGuestsTabName || 'registered guests').toLowerCase() }} for this property. To add more, delete an existing {{ registeredGuestsSingular.toLowerCase() }}.
          </div>
        </div>
      </v-card>
    </div>

    <div v-else-if="noProperty === true" class="d-flex flex-column align-center mt-5">
      <h3 style="color: white;">Something went wrong. No property found!</h3>

      <br>

      <v-btn color="accent" class="mx-auto" style="min-width: 150px;" @click="$router.back()">
        <v-icon class="mr-1">mdi-arrow-left</v-icon>
        <span>Back</span>
      </v-btn>
    </div>

    <!-- Add/Edit Guest Dialog -->
    <v-dialog v-model="guestDialog" max-width="500px" scrollable>
      <v-card class="py-5">
        <v-card-title class="d-flex justify-center pa-0 mb-5">
          <h3>{{ editingGuest ? 'Edit' : 'Add' }} {{ registeredGuestsSingular }}</h3>
        </v-card-title>

        <v-card-text style="max-height: 500px;">
          <v-form ref="guestForm" @submit.prevent="saveGuest">
            <div class="d-flex flex-column flex-md-row" :style="{ gap: mobileWidth ? '0px' : '1.5rem' }">
              <v-text-field v-model="guestModel.fname" label="First Name" :rules="[
                v => !!v || 'First name is required',
                v => (v && v.length <= 32) || 'First name must be less than 33 characters'
              ]" required />

              <v-text-field v-model="guestModel.lname" label="Last Name" :rules="[
                v => !!v || 'Last name is required',
                v => (v && v.length <= 64) || 'Last name must be less than 65 characters'
              ]" required />
            </div>

            <v-select v-model="guestModel.affiliation" label="Affiliation" :items="affiliationOptions" :rules="[
              v => !!v || 'Affiliation is required',
              v => (v && v.length <= 64) || 'Affiliation must be less than 65 characters'
            ]" required />

            <!-- Legal Terms Section -->
            <div v-if="hasLegalTerms" class="legal-terms-section mt-5">
              <div class="legal-terms-text mb-4">
                <!-- sanitized, formatted legal terms -->
                <div v-html="formattedLegalHTML"></div>
              </div>

              <div class="legal-checkboxes">
                <v-checkbox
                  v-model="guestModel.legalCheck1"
                  class="mt-2"
                  hide-details
                >
                  <template v-slot:label>
                    <span class="text-body-2">I have read and understand the Snowline Rules and Regulations.</span>
                  </template>
                </v-checkbox>

                <v-checkbox
                  v-model="guestModel.legalCheck2"
                  class="mt-2"
                  hide-details
                >
                  <template v-slot:label>
                    <span class="text-body-2">The information I have provided on this form is accurate.</span>
                  </template>
                </v-checkbox>

                <v-checkbox
                  v-model="guestModel.legalCheck3"
                  class="mt-2"
                  hide-details
                >
                  <template v-slot:label>
                    <span class="text-body-2">
                      A lot may revise its {{ env.registeredGuestsTabName || 'Registered Guests' }} once every
                      {{ changeFrequencyMonths > 0 ? changeFrequencyMonths : registeredGuestsChangeTime }}
                      {{ changeFrequencyMonths > 0 ? (changeFrequencyMonths === 1 ? 'month' : 'months') : 'days' }}.
                    </span>
                  </template>
                </v-checkbox>
              </div>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="d-flex justify-space-around pb-0 mt-3">
          <v-btn color="primary" text @click="guestDialog = false">Cancel</v-btn>

          <v-btn
            @click="saveGuest"
            color="primary"
            :loading="saving"
            :disabled="!canSaveGuest"
            style="min-width: 120px;"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          Confirm Delete
        </v-card-title>

        <v-card-text class="mt-2">
          Are you sure you want to delete <b>{{ guestToDelete ? guestToDelete.fname + ' ' + guestToDelete.lname : ''
            }}</b>?
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
/* IMPORTS */
import AlertsPanel from '@/components/AlertsPanel.vue';
import FormDialog from '@/components/crud-management/children/FormDialog';
import fetchV2 from '@/assets/js/fetchV2';

export default {
  name: '',

  components: {
    AlertsPanel,
    FormDialog,
  },

  props: {
    env: {
      type: Object,
      required: true,
    },

    permissions: {
      type: Number,
      required: true,
    },

    overduePayments: {
      type: Array,
      required: true,
    },

    upcomingPayments: {
      type: Array,
      required: true,
    },

    generalAlerts: {
      type: Array,
      required: true,
    },

    properties: {
      type: Array,
      required: true,
    },

    registeredGuests: {
      type: Array,
      required: false,
      default: () => [],
    },

    globals: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },

  data: () => ({
    dialogKey: new Date().getTime(),
    property: null,
    connectedUsersFI: {
      user_id: {
        label: 'Email',
        type: 'email',
        required: true,
        email: true
      },
    },
    overduePaymentsProp: [],
    upcomingPaymentsProp: [],
    connectedUserLoading: false,
    connectedUsersModel: {},
    connectedUsersDialog: false,
    connectedUsersDict: [],
    disabled: false,
    noProperty: null,
    proppop: false,
    filteredGuests: [],
    guestDialog: false,
    deleteDialog: false,
    editingGuest: null,
    guestToDelete: null,
    guestModel: {
      fname: '',
      lname: '',
      affiliation: '',
      legalCheck1: false,
      legalCheck2: false,
      legalCheck3: false,
    },
    affiliationOptions: [
      {
        text: 'Owner',
        value: 'Owner',
      },
      {
        text: 'Family Member',
        value: 'Family',
      },
      {
        text: 'Resident',
        value: 'Resident',
      },
    ],
    saving: false,
    deleting: false,
  }),

  watch: {
    properties(val) {
      if (val.length > 0 && this.$route.params.property_id != null && val.filter(f => f.id == this.$route.params.property_id).length > 0 && !this.proppop) {
        this.proppop = true;
        this.noProperty = false;
        this.property = val.filter(f => f.id == this.$route.params.property_id)[0];
        this.filterGuests();
      }
      else {
        this.noProperty = true;
      }
    },
    overduePayments: function (val) {
      this.updateAlerts();
    },

    upcomingPayments: function (val) {
      this.updateAlerts();
    },

    registeredGuests(val) {
      // Re-filter when registeredGuests prop changes
      this.filterGuests();
    },

    // Re-filter when the route property_id changes (e.g., user navigates to a different property)
    '$route.params.property_id': function () {
      this.filterGuests();
    },
  },

  computed: {
    mobileWidth() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    maxRegisteredGuests() {
      return this.globals && this.globals.registered_guests_max
        ? parseInt(this.globals.registered_guests_max)
        : 10;
    },

    registeredGuestsChangeTime() {
      return this.globals && this.globals.registered_guests_change
        ? parseInt(this.globals.registered_guests_change)
        : 0;
    },

    canAddGuest() {
      return this.filteredGuests.length < this.maxRegisteredGuests;
    },

    maxGuestsReached() {
      return this.filteredGuests.length >= this.maxRegisteredGuests;
    },

    registeredGuestsSingular() {
      const tabName = this.env.registeredGuestsTabName || 'Registered Guest';
      // Remove trailing 's' if present for singular form
      return tabName.endsWith('s') ? tabName.slice(0, -1) : tabName;
    },

    hasLegalTerms() {
      return this.globals && this.globals.registered_guests_legal && this.globals.registered_guests_legal.length > 0;
    },

    legalTermsText() {
      if (!this.hasLegalTerms) return '';
      return this.globals.registered_guests_legal;
    },

    // Returns sanitized HTML where:
    // - escaped "\\n" and real newlines are normalized
    // - consecutive blank lines become paragraph (<p>) blocks
    // - any original HTML is escaped to avoid XSS
    formattedLegalHTML() {
      if (!this.hasLegalTerms) return '';
      const raw = this.legalTermsText || '';

      // Normalize escaped sequences (\\n, \r\n, \r) into real newlines
      let s = String(raw).replace(/\\r\\n/g, '\n').replace(/\\r/g, '\n').replace(/\\n/g, '\n');

      // Helper: escape HTML
      const escapeHtml = (str) => {
        return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      };

      // Split into paragraph blocks where there are 2+ newlines
      const paragraphs = s.split(/\n{2,}/g).map(p => p.trim()).filter(p => p.length > 0);

      // For each paragraph, escape HTML and convert single newlines to <br>
      const html = paragraphs.map(p => {
        const escaped = escapeHtml(p).replace(/\n/g, '<br>');
        return `<p>${escaped}</p>`;
      }).join('');

      return html;
    },

    changeFrequencyMonths() {
      return this.registeredGuestsChangeTime > 0 ? Math.round(this.registeredGuestsChangeTime / 30) : 0;
    },

    canSaveGuest() {
      if (!this.hasLegalTerms) return true;
      return this.guestModel.legalCheck1 && this.guestModel.legalCheck2 && this.guestModel.legalCheck3;
    },
  },

  methods: {
    updateAlerts() {
      this.overduePaymentsProp = this.overduePayments.filter(f => f.property_id == this.$route.params.property_id);
      this.upcomingPaymentsProp = this.upcomingPayments.filter(f => f.property_id == this.$route.params.property_id);
    },

    filterGuests() {
      // Determine the property id to filter by. Prefer the loaded `property.id` if available,
      // otherwise fall back to the route param. Normalize to integer for reliable comparison.
      const routePropId = this.$route && this.$route.params && this.$route.params.property_id
        ? parseInt(this.$route.params.property_id)
        : null;

      const activePropId = this.property && this.property.id ? parseInt(this.property.id) : routePropId;

      if (!activePropId || !Array.isArray(this.registeredGuests)) {
        this.filteredGuests = [];
        return;
      }

      // Filter guests by property id. Accept common field name variations and normalize before comparing.
      this.filteredGuests = this.registeredGuests.filter(g => {
        if (!g) return false;
        // Try property_id, then propertyId, then property
        const gidRaw = g.property_id !== undefined ? g.property_id : (g.propertyId !== undefined ? g.propertyId : g.property);
        if (gidRaw === undefined || gidRaw === null) return false;
        const gid = parseInt(gidRaw);
        return !isNaN(gid) && gid === activePropId;
      });
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString();
    },

    canEditGuest(guest) {
      // If no lock configured, always editable
      if (this.registeredGuestsChangeTime === 0) {
        return true;
      }

      const ts = this._parseTimestamp(guest && guest.modified_on ? guest.modified_on : guest.created_at);
      if (isNaN(ts)) return true; // if we can't parse, default to editable to avoid blocking users

      const now = Date.now();
      const daysSinceCreation = (now - ts) / (1000 * 60 * 60 * 24);

      return !!(daysSinceCreation >= this.registeredGuestsChangeTime);
    },

    // Returns number of whole days remaining until this guest can be edited/deleted.
    // 0 means editable now.
    guestDaysRemaining(guest) {
      if (!guest) return 0;
      if (this.registeredGuestsChangeTime === 0) return 0;
      const ts = this._parseTimestamp(guest.modified_on ? guest.modified_on : guest.created_at);
      if (isNaN(ts)) return 0;
      const now = Date.now();
      const daysSinceCreation = (now - ts) / (1000 * 60 * 60 * 24);
      const remaining = Math.ceil(this.registeredGuestsChangeTime - daysSinceCreation);
      return remaining > 0 ? remaining : 0;
    },

    // Internal helper: parse various timestamp formats (ms, seconds, ISO string)
    _parseTimestamp(raw) {
      if (raw === null || raw === undefined) return NaN;

      // numeric values (could be seconds or milliseconds)
      if (typeof raw === 'number') {
        // if seconds (roughly 10 digits), convert to ms
        return raw < 1e12 ? raw * 1000 : raw;
      }

      // numeric string
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (/^\d+$/.test(trimmed)) {
          const n = parseInt(trimmed, 10);
          return n < 1e12 ? n * 1000 : n;
        }

        const parsed = Date.parse(trimmed);
        return isNaN(parsed) ? NaN : parsed;
      }

      return NaN;
    },

    addGuest() {
      if (!this.canAddGuest) {
        const tabName = this.env.registeredGuestsTabName || 'registered guests';
        this.$emit('error', `Maximum number of ${tabName.toLowerCase()} reached for this property.`);
        return;
      }

      this.editingGuest = null;
      this.guestModel = {
        fname: '',
        lname: '',
        affiliation: '',
        legalCheck1: false,
        legalCheck2: false,
        legalCheck3: false,
      };
      this.guestDialog = true;
    },

    editGuest(guest) {
      if (!this.canEditGuest(guest)) {
        const daysRemaining = Math.ceil(
          this.registeredGuestsChangeTime -
          ((new Date() - new Date(guest.modified_on ? guest.modified_on : guest.created_at)) / (1000 * 60 * 60 * 24))
        );
        this.$emit('error', `This guest cannot be edited for ${daysRemaining} more day(s).`);
        return;
      }

      this.editingGuest = guest;
      this.guestModel = {
        fname: guest.fname,
        lname: guest.lname,
        affiliation: guest.affiliation || '',
        legalCheck1: false,
        legalCheck2: false,
        legalCheck3: false,
      };
      this.guestDialog = true;
    },

    deleteGuest(guest) {
      if (!this.canEditGuest(guest)) {
        const daysRemaining = Math.ceil(
          this.registeredGuestsChangeTime -
          ((new Date() - new Date(guest.modified_on ? guest.modified_on : guest.created_at)) / (1000 * 60 * 60 * 24))
        );
        this.$emit('error', `This guest cannot be deleted for ${daysRemaining} more day(s).`);
        return;
      }

      this.guestToDelete = guest;
      this.deleteDialog = true;
    },

    async saveGuest() {
      if (!this.$refs.guestForm.validate()) {
        return;
      }

      this.saving = true;

      try {
        const body = {
          id: this.editingGuest ? this.editingGuest.id : null,
          property_id: parseInt(this.$route.params.property_id),
          fname: this.guestModel.fname,
          lname: this.guestModel.lname,
          affiliation: this.guestModel.affiliation,
        };

        if (this.editingGuest) {
          // Update existing guest
          await fetchV2(`PUT /registered_guests/personal`, body);
          const tabName = this.registeredGuestsSingular;
          this.$emit('success', {
            message: `${tabName} updated successfully.`,
            postCall: 'refresh-registered-guests',
          });
        } else {
          // Add new guest
          await fetchV2('POST /registered_guests/personal', body);
          const tabName = this.registeredGuestsSingular;
          this.$emit('success', {
            message: `${tabName} added successfully.`,
            postCall: 'refresh-registered-guests',
          });
        }

        this.guestDialog = false;
      } catch (err) {
        console.error(err);
        if (err && err.message) {
          this.$emit('error', err.message);
        } else {
          const tabName = this.registeredGuestsSingular;
          this.$emit('error', `An error occurred while saving the ${tabName.toLowerCase()}.`);
        }
      } finally {
        this.saving = false;
      }
    },

    async confirmDelete() {
      if (!this.guestToDelete) return;

      this.deleting = true;

      const body = {
        id: this.guestToDelete.id,
      };

      try {
        await fetchV2(`DELETE /registered_guests/personal`, body);
        const tabName = this.registeredGuestsSingular;
        this.$emit('success', {
          message: `${tabName} deleted successfully.`,
          postCall: 'refresh-registered-guests',
        });
        this.deleteDialog = false;
        this.guestToDelete = null;
      } catch (err) {
        console.error(err);
        if (err && err.message) {
          this.$emit('error', err.message);
        } else {
          const tabName = this.registeredGuestsSingular;
          this.$emit('error', `An error occurred while deleting the ${tabName.toLowerCase()}.`);
        }
      } finally {
        this.deleting = false;
      }
    },

    toTel: (s) => {
      if (!s) return '';
      let f = s.replace(/[\x2b\x28\x29\x2d\x20]/g, '');
      let t = '';
      for (let i = 0; i < f.length; i++) {
        if (i === 1) t += ' ('; if (i === 4) t += ') '; if (i === 7) t += '-';
        t += f[i];
      }
      return t;
    },

    getPropertyURL: (id) => {
      return 'https://property.whatcomcounty.us/propertyaccess/Property.aspx?cid=0&year=' + (new Date().getFullYear()) + '&prop_id=' + id;
    },
  },

  created() {
    if (this.properties.length > 0 && this.properties.filter(f => f.id == this.$route.params.property_id).length > 0) {
      this.proppop = true;
      this.noProperty = false;
      this.property = this.properties.filter(f => { return f.id == this.$route.params.property_id })[0]
    }
    else if (this.$route.params.property_id == null) {
      this.noProperty = true;
    }
    this.updateAlerts();
    this.filterGuests();
  },

};
</script>

<style>
.v-expansion-panel-content__wrap {
  /* slightly reduced bottom padding for expansion panels */
  padding: 0 0 8px 0 !important;
}

.hover-highlight:hover {
  background: #eee;
}

/* Registered Guests Section - Compact Modern Design */
.guests-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.guests-header h2 {
  margin: 0 0 6px 0;
  text-align: left;
  font-size: 22px;
  font-weight: 600;
}

.guests-description {
  margin: 0;
  font-size: 13px;
  color: #666;
  line-height: 1.4;
}

.guests-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.guest-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.guest-card:hover {
  background-color: #f0f1f3;
  border-color: #d0d0d0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.guest-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.guest-name {
  font-size: 15px;
  font-weight: 600;
  color: #222;
  line-height: 1.3;
}

.guest-affiliation {
  font-size: 13px;
  color: #444;
}

.guest-meta {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.meta-divider {
  color: #999;
  margin: 0 2px;
}

.guest-locked-badge {
  color: #d32f2f;
  font-weight: 500;
}

.guest-actions {
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  margin-left: 12px;
}

.guests-empty {
  text-align: center;
  padding: 24px;
  color: #666;
  font-size: 14px;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 16px;
}

.guests-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 8px;
}

.max-guests-message {
  font-size: 13px;
  color: #666;
  max-width: 600px;
  text-align: center;
  line-height: 1.5;
  margin-top: 8px;
}

/* Legal Terms Section */
.legal-terms-section {
  background-color: #f8f9fa;
  padding: 16px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.legal-terms-text {
  font-size: 13px;
  line-height: 1.6;
  color: #333;
  padding: 12px;
  background-color: #fff;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.legal-checkboxes .v-input--checkbox {
  margin-top: 0;
}

.legal-checkboxes .text-body-2 {
  font-size: 13px;
  line-height: 1.5;
  color: #333;
}

/* Mobile responsive adjustments */
@media (max-width: 600px) {
  .guests-header h2 {
    font-size: 20px;
  }

  .guests-description {
    font-size: 12px;
  }

  .guest-card {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
    gap: 10px;
  }

  .guest-info {
    gap: 6px;
  }

  .guest-name {
    font-size: 14px;
  }

  .guest-meta {
    font-size: 11px;
  }

  .guest-actions {
    margin-left: 0;
    justify-content: flex-start;
    padding-top: 4px;
    border-top: 1px solid #e0e0e0;
    gap: 6px;
  }

  .max-guests-message {
    font-size: 12px;
  }
}

/* Tablet adjustments */
@media (min-width: 601px) and (max-width: 960px) {
  .guest-card {
    padding: 10px 14px;
  }

  .guest-name {
    font-size: 14px;
  }
}
</style>
